import type { Coords, Placement } from '../types.js';

// ─────────────────────────────────────────────────────────────────────────────
// Shadow DOM Traversal
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Recursively walk every shadow root in the document to find an element by id.
 *
 * Standard querySelector / getElementById cannot cross shadow boundaries.
 * Strategy:
 *   1. Fast path — id-attribute selector on the current root.
 *   2. Slow path — iterate all elements looking for nested shadowRoots, recurse.
 */
export function deepQueryById(
  root: Document | ShadowRoot,
  id: string,
): Element | null {
  const direct = root.querySelector(`[id="${CSS.escape(id)}"]`);
  if (direct) return direct;

  for (const el of Array.from(root.querySelectorAll('*'))) {
    if ((el as Element & { shadowRoot?: ShadowRoot }).shadowRoot) {
      const found = deepQueryById(
        (el as Element & { shadowRoot: ShadowRoot }).shadowRoot,
        id,
      );
      if (found) return found;
    }
  }
  return null;
}

// ─────────────────────────────────────────────────────────────────────────────
// Placement maths
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Compute raw (unclamped) top/left fixed coordinates for the panel
 * given the trigger's bounding rect, the panel's bounding rect,
 * the desired placement string, and the gap offset in pixels.
 */
export function computeCoords(
  tRect:     DOMRect,
  pRect:     DOMRect,
  placement: Placement,
  offset:    number,
): Coords {
  const [side, align = 'center'] = placement.split('-') as [string, string];
  let top = 0, left = 0;

  switch (side) {
    case 'top':    top  = tRect.top    - pRect.height - offset; break;
    case 'bottom': top  = tRect.bottom + offset;                break;
    case 'left':   left = tRect.left   - pRect.width  - offset; break;
    case 'right':  left = tRect.right  + offset;                break;
  }

  if (side === 'top' || side === 'bottom') {
    if      (align === 'start') left = tRect.left;
    else if (align === 'end')   left = tRect.right  - pRect.width;
    else                        left = tRect.left + (tRect.width  - pRect.width)  / 2;
  } else {
    if      (align === 'start') top  = tRect.top;
    else if (align === 'end')   top  = tRect.bottom - pRect.height;
    else                        top  = tRect.top  + (tRect.height - pRect.height) / 2;
  }

  return { top, left };
}

/**
 * Clamp panel coordinates so it stays within the visible viewport
 * with an 8 px safety margin on every edge.
 */
export function clampToViewport(
  { top, left }: Coords,
  pRect: DOMRect,
  margin = 8,
): Coords {
  return {
    top:  Math.max(margin, Math.min(top,  window.innerHeight - pRect.height - margin)),
    left: Math.max(margin, Math.min(left, window.innerWidth  - pRect.width  - margin)),
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Misc helpers
// ─────────────────────────────────────────────────────────────────────────────

/** Escape a string for safe text-node insertion (prevents XSS via label). */
export function escapeHtml(str: string): string {
  return str
    .replace(/&/g,  '&amp;')
    .replace(/</g,  '&lt;')
    .replace(/>/g,  '&gt;')
    .replace(/"/g,  '&quot;')
    .replace(/'/g,  '&#039;');
}

/** Emit a typed CustomEvent from a host element. */
export function emit<T>(
  host:    Element,
  name:    string,
  detail?: T,
  opts:    Partial<CustomEventInit> = {},
): CustomEvent<T> {
  const event = new CustomEvent<T>(name, {
    detail,
    bubbles:  true,
    composed: true,
    ...opts,
  });
  host.dispatchEvent(event);
  return event;
}
